import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import {
  EMPTY_ARRAY,
  useDefaultMarketAccounts,
  useMarketAccountsContext,
  useMarketsContext,
  type MarketAccount,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';
import { getPredictionMarketAccountsForMarket } from '../../../PredictionMarkets/PredictionCard/predictionCardMarkets';
import {
  getPredictionMarketsPriceGridMarket,
  getPredictionMarketsPriceGridMarketAccount,
  resolvePredictionMarketsPriceGridMarket,
  resolvePredictionMarketsPriceGridMarketAccount,
  setPredictionMarketsPriceGridSelections,
} from './marketSelection';
import type { PredictionMarketsPriceGridFilterState } from './types';

export interface PredictionMarketOption {
  name: string;
  label: string;
}

export function usePredictionMarketsPriceGridMarketSelection({
  filterState,
  onFilterStateChanged,
}: {
  filterState: PredictionMarketsPriceGridFilterState;
  onFilterStateChanged: (filterState: PredictionMarketsPriceGridFilterState) => void;
}) {
  const { isRefDataReady, refDataIndexes } = usePredictionsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const { defaultMarketAccounts } = useDefaultMarketAccounts();
  const { isMarketAccountActive, marketAccountsByMarket } = useMarketAccountsContext();
  const supportedMarkets = isRefDataReady ? refDataIndexes.supportedMarkets : EMPTY_ARRAY;

  const marketOptions = useMemo(
    () =>
      supportedMarkets.map(name => ({
        name,
        label: marketDisplayNameByName.get(name) ?? name,
      })),
    [marketDisplayNameByName, supportedMarkets]
  );
  const selectedMarketName = useMemo(
    () => resolvePredictionMarketsPriceGridMarket(getPredictionMarketsPriceGridMarket(filterState), supportedMarkets),
    [filterState, supportedMarkets]
  );
  const selectedMarket = useMemo(
    () => marketOptions.find(option => option.name === selectedMarketName),
    [marketOptions, selectedMarketName]
  );

  const getMarketAccountOptions = useCallback(
    (market: string | undefined) =>
      getPredictionMarketAccountsForMarket(market, marketAccountsByMarket).filter(isMarketAccountActive),
    [isMarketAccountActive, marketAccountsByMarket]
  );
  const marketAccountOptions = useMemo(
    () => getMarketAccountOptions(selectedMarketName),
    [getMarketAccountOptions, selectedMarketName]
  );
  const selectedMarketAccountName = useMemo(
    () =>
      resolvePredictionMarketsPriceGridMarketAccount(
        getPredictionMarketsPriceGridMarketAccount(filterState),
        marketAccountOptions,
        selectedMarketName ? defaultMarketAccounts[selectedMarketName] : undefined
      ),
    [defaultMarketAccounts, filterState, marketAccountOptions, selectedMarketName]
  );
  const selectedMarketAccount = useMemo(
    () => marketAccountOptions.find(option => option.Name === selectedMarketAccountName),
    [marketAccountOptions, selectedMarketAccountName]
  );

  // Normalize missing or stale persisted selections once prediction reference data is ready.
  useEffect(() => {
    if (!isRefDataReady) {
      return;
    }
    const persistedMarket = getPredictionMarketsPriceGridMarket(filterState);
    const persistedMarketAccount = getPredictionMarketsPriceGridMarketAccount(filterState);
    if (persistedMarket === selectedMarketName && persistedMarketAccount === selectedMarketAccountName) {
      return;
    }
    onFilterStateChanged(
      setPredictionMarketsPriceGridSelections(filterState, selectedMarketName, selectedMarketAccountName)
    );
  }, [filterState, isRefDataReady, onFilterStateChanged, selectedMarketAccountName, selectedMarketName]);

  const onMarketChanged = useCallback(
    (market: PredictionMarketOption | undefined) => {
      const marketAccountOptions = getMarketAccountOptions(market?.name);
      const marketAccount = resolvePredictionMarketsPriceGridMarketAccount(
        undefined,
        marketAccountOptions,
        market ? defaultMarketAccounts[market.name] : undefined
      );
      onFilterStateChanged(setPredictionMarketsPriceGridSelections(filterState, market?.name, marketAccount));
    },
    [defaultMarketAccounts, filterState, getMarketAccountOptions, onFilterStateChanged]
  );

  const onMarketAccountChanged = useCallback(
    (marketAccount: MarketAccount | undefined) => {
      onFilterStateChanged(
        setPredictionMarketsPriceGridSelections(filterState, selectedMarketName, marketAccount?.Name)
      );
    },
    [filterState, onFilterStateChanged, selectedMarketName]
  );

  return useMemo(
    () => ({
      market: selectedMarketName,
      marketOptions,
      selectedMarket,
      showMarketSelector: marketOptions.length > 1,
      onMarketChanged,
      marketAccountOptions,
      selectedMarketAccount,
      marketAccountSelectorDisabled: marketAccountOptions.length <= 1,
      onMarketAccountChanged,
    }),
    [
      marketAccountOptions,
      marketOptions,
      onMarketAccountChanged,
      onMarketChanged,
      selectedMarket,
      selectedMarketAccount,
      selectedMarketName,
    ]
  );
}

export type PredictionMarketsPriceGridMarketSelection = ReturnType<typeof usePredictionMarketsPriceGridMarketSelection>;
